import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Card, CardHeader } from '@/components/common/Card';
import { ConfirmDialog, Modal } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { studentProfileApi } from '@/services/api/endpoints/studentProfile';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { IdentificationType, StudentIdentification } from '@/types/domain';
import { formatDate, humanise, orDash } from '@/utils/format';

const TYPES: IdentificationType[] = [
  'BIRTH_CERTIFICATE',
  'NATIONAL_ID',
  'PASSPORT',
  'RESIDENCE_PERMIT',
  'OTHER',
];

/**
 * Identity documents held for a student.
 *
 * A list rather than columns on the student record: a child legitimately has a
 * birth certificate and a passport, and flattening them onto one row loses
 * whichever arrives second.
 */
export function StudentIdentificationsPanel({ studentId }: { studentId: string }) {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [formOpen, setFormOpen] = useState(false);
  const [deleting, setDeleting] = useState<StudentIdentification | null>(null);
  const [form, setForm] = useState({
    identificationType: 'BIRTH_CERTIFICATE' as IdentificationType,
    identificationNumber: '',
    issuingCountry: '',
    issuedDate: '',
    expiryDate: '',
    primary: false,
  });

  const query = useQuery({
    queryKey: queryKeys.studentProfile.identifications(studentId),
    queryFn: () => studentProfileApi.identifications(studentId),
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: queryKeys.studentProfile.all });

  const add = useMutation({
    mutationFn: () =>
      studentProfileApi.addIdentification(studentId, {
        identificationType: form.identificationType,
        identificationNumber: form.identificationNumber,
        issuingCountry: form.issuingCountry === '' ? undefined : form.issuingCountry,
        issuedDate: form.issuedDate === '' ? undefined : form.issuedDate,
        expiryDate: form.expiryDate === '' ? undefined : form.expiryDate,
        primary: form.primary,
      }),
    onSuccess: () => {
      toast.success('Identification recorded');
      setFormOpen(false);
      setForm({ ...form, identificationNumber: '', issuedDate: '', expiryDate: '' });
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const remove = useMutation({
    mutationFn: (id: string) => studentProfileApi.removeIdentification(studentId, id),
    onSuccess: () => {
      toast.success('Identification removed');
      setDeleting(null);
      invalidate();
    },
    onError: (error) => {
      handleApiError(error);
      setDeleting(null);
    },
  });

  return (
    <>
      <Card>
        <CardHeader
          title="Identification"
          description="A document number is unique per school, type and issuing country."
          action={
            can(PERMISSIONS.STUDENT_UPDATE) ? (
              <Button size="sm" variant="secondary" onClick={() => setFormOpen(true)}>
                Add
              </Button>
            ) : undefined
          }
        />

        {query.isPending ? (
          <LoadingState label="Loading identification" />
        ) : query.error !== null ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : query.data === undefined || query.data.length === 0 ? (
          <p className="py-6 text-center text-sm text-subtle">No identity documents recorded.</p>
        ) : (
          <ul className="divide-y divide-line">
            {query.data.map((identification) => (
              <li key={identification.id} className="flex items-start justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
                    {humanise(identification.identificationType)}
                    {identification.primary && <Badge tone="accent">Primary</Badge>}
                    {identification.expired && <Badge tone="danger">Expired</Badge>}
                  </p>
                  <p className="mt-0.5 font-mono text-xs text-muted">
                    {identification.identificationNumber}
                  </p>
                  <p className="mt-0.5 text-xs text-subtle">
                    {orDash(identification.issuingCountry)}
                    {identification.expiryDate !== undefined &&
                      ` · expires ${formatDate(identification.expiryDate)}`}
                  </p>
                </div>
                {can(PERMISSIONS.STUDENT_UPDATE) && (
                  <Button size="sm" variant="ghost" onClick={() => setDeleting(identification)}>
                    Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title="Record an identity document"
        footer={
          <>
            <Button variant="ghost" onClick={() => setFormOpen(false)} disabled={add.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => add.mutate()}
              loading={add.isPending}
              disabled={form.identificationNumber.trim() === ''}
            >
              Add
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <SelectField
            label="Type"
            required
            value={form.identificationType}
            onChange={(event) =>
              setForm({ ...form, identificationType: event.target.value as IdentificationType })
            }
          >
            {TYPES.map((type) => (
              <option key={type} value={type}>
                {humanise(type)}
              </option>
            ))}
          </SelectField>

          <TextField
            label="Number"
            required
            value={form.identificationNumber}
            onChange={(event) => setForm({ ...form, identificationNumber: event.target.value })}
          />

          <TextField
            label="Issuing country"
            value={form.issuingCountry}
            onChange={(event) => setForm({ ...form, issuingCountry: event.target.value })}
            hint="Part of the uniqueness check: two countries issue national IDs from independent ranges"
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="Issued"
              type="date"
              value={form.issuedDate}
              onChange={(event) => setForm({ ...form, issuedDate: event.target.value })}
            />
            <TextField
              label="Expires"
              type="date"
              value={form.expiryDate}
              onChange={(event) => setForm({ ...form, expiryDate: event.target.value })}
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 border-line text-accent focus:ring-accent"              checked={form.primary}
              onChange={(event) => setForm({ ...form, primary: event.target.checked })}
            />
            Primary identification
          </label>
        </div>
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        destructive
        title="Remove this identification?"
        message={`${humanise(deleting?.identificationType)} ${deleting?.identificationNumber ?? ''} will be removed from this student's record.`}
        confirmLabel="Remove"
        loading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting !== null) {
            remove.mutate(deleting.id);
          }
        }}
      />
    </>
  );
}
